import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Gavel,
  Gift,
  Layers,
  Loader2,
  LogIn,
  Package,
  Receipt,
  Search,
  ShoppingBag,
  Store,
  Ticket,
  Trophy,
} from "lucide-react";
import { desktopNavItems } from "../../config/storefront-nav";
import {
  fetchStorefrontSearch,
  STOREFRONT_SEARCH_KIND_LABEL,
  type StorefrontSearchKind,
} from "../../lib/storefront-search";
import { useAuth } from "../../stores/auth";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type PaletteGroup = "Results" | "Pages" | "Actions";

interface PaletteEntry {
  id: string;
  label: string;
  detail?: string;
  path?: string;
  group: PaletteGroup;
  keywords: string[];
  action?: () => void;
  kind?: StorefrontSearchKind;
}

const GROUP_ORDER: PaletteGroup[] = ["Results", "Pages", "Actions"];

const KIND_ICONS: Record<StorefrontSearchKind, typeof ShoppingBag> = {
  product: ShoppingBag,
  listing: ShoppingBag,
  store: Store,
  pack: Gift,
  raffle: Ticket,
  auction: Gavel,
  page: FileText,
  order: Receipt,
};

function matchesQuery(entry: PaletteEntry, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  if (entry.label.toLowerCase().includes(q)) return true;
  if (entry.detail?.toLowerCase().includes(q)) return true;
  return entry.keywords.some((keyword) => keyword.toLowerCase().includes(q));
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user, moduleEnabled, config } = useAuth();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = globalThis.setTimeout(() => setDebouncedQuery(query.trim()), 280);
    return () => globalThis.clearTimeout(timer);
  }, [query]);

  const staticEntries = useMemo((): PaletteEntry[] => {
    const pages: PaletteEntry[] = desktopNavItems(moduleEnabled, !!user, config).map((item) => ({
      id: item.to,
      label: item.label,
      path: item.to,
      group: "Pages",
      keywords: [item.label, item.to, item.module ?? ""],
    }));

    const actions: PaletteEntry[] = [];

    if (!user) {
      actions.push({
        id: "action-sign-in",
        label: "Sign in",
        path: "/login",
        group: "Actions",
        keywords: ["login", "sign in", "account"],
      });
    }

    if (user && moduleEnabled("ecommerce")) {
      actions.push(
        {
          id: "action-cart",
          label: "Cart",
          path: "/cart",
          group: "Actions",
          keywords: ["cart", "checkout", "bag"],
        },
        {
          id: "action-orders",
          label: "Your orders",
          path: "/orders",
          group: "Actions",
          keywords: ["orders", "purchases"],
        },
      );
    }

    if (moduleEnabled("ecommerce")) {
      actions.push({
        id: "action-track",
        label: "Track an order",
        path: "/track",
        group: "Actions",
        keywords: ["track", "shipping", "delivery", "order number"],
      });
    }

    if (user && moduleEnabled("collection")) {
      actions.push({
        id: "action-collection",
        label: "Your collection",
        path: "/inventory",
        group: "Actions",
        keywords: ["inventory", "vault", "cards", "collection"],
      });
    }

    if (moduleEnabled("packs")) {
      actions.push({
        id: "action-leaderboard",
        label: "Pack leaderboard",
        path: "/leaderboard",
        group: "Actions",
        keywords: ["leaderboard", "rank", "pulls"],
      });
    }

    return [...pages, ...actions];
  }, [moduleEnabled, user, config]);

  const recordSearch = useQuery({
    queryKey: ["storefront-search", debouncedQuery],
    queryFn: () => fetchStorefrontSearch(debouncedQuery),
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  const recordEntries = useMemo((): PaletteEntry[] => {
    if (debouncedQuery.length < 2) return [];
    return (recordSearch.data ?? []).map((item) => ({
      id: `${item.kind}:${item.id}`,
      label: item.title,
      detail: item.subtitle || STOREFRONT_SEARCH_KIND_LABEL[item.kind],
      path: item.path,
      group: "Results",
      keywords: [item.title, item.subtitle, item.kind],
      kind: item.kind,
    }));
  }, [debouncedQuery.length, recordSearch.data]);

  const filtered = useMemo(() => {
    const trimmed = query.trim();
    const staticMatches = staticEntries.filter((entry) => matchesQuery(entry, query));
    const records = trimmed.length >= 2 ? recordEntries : [];

    return GROUP_ORDER.flatMap((group) => {
      if (group === "Results") return records;
      return staticMatches.filter((entry) => entry.group === group);
    });
  }, [staticEntries, recordEntries, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setHighlight(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query, recordSearch.data]);

  const selectEntry = useCallback(
    (entry: PaletteEntry) => {
      if (entry.action) {
        entry.action();
      } else if (entry.path) {
        navigate(entry.path);
      }
      onClose();
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((value) => Math.min(value + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((value) => Math.max(value - 1, 0));
        return;
      }
      if (event.key === "Enter" && filtered[highlight]) {
        event.preventDefault();
        selectEntry(filtered[highlight]!);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, filtered, highlight, onClose, selectEntry]);

  if (!open) return null;

  const grouped = GROUP_ORDER.map((group) => ({
    group,
    items: filtered.filter((entry) => entry.group === group),
  })).filter((section) => section.items.length > 0);

  const searching = query.trim().length >= 2 && recordSearch.isFetching;
  const showEmpty =
    grouped.length === 0 && !searching && (query.trim().length >= 2 || query.trim().length === 0);

  let flatIndex = -1;

  return createPortal(
    <div
      className="fixed inset-0 z-[10060] flex items-start justify-center bg-black/65 p-4 pt-[10vh] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-brand-dark shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
          <Search size={18} className="shrink-0 text-white/40" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search shop, marketplace, stores, packs…"
            className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/35"
          />
          {searching ? (
            <Loader2 size={16} className="shrink-0 animate-spin text-white/40" aria-hidden />
          ) : (
            <kbd className="hidden rounded-md border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-white/45 sm:inline">
              Esc
            </kbd>
          )}
        </div>

        <div className="max-h-[min(380px,52vh)] overflow-y-auto py-1">
          {showEmpty ? (
            <p className="px-4 py-6 text-center text-sm text-white/45">
              {query.trim().length >= 2 ? "No matches — try another term" : "Type to search or pick a destination"}
            </p>
          ) : (
            grouped.map((section) => (
              <div key={section.group} className="py-1">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/30">
                  {section.group}
                </p>
                {section.items.map((entry) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const selected = index === highlight;
                  const KindIcon = entry.kind ? KIND_ICONS[entry.kind] : null;
                  const PageIcon =
                    entry.group === "Pages"
                      ? entry.path === "/packs"
                        ? Gift
                        : entry.path === "/leaderboard"
                          ? Trophy
                          : entry.path === "/inventory"
                            ? Layers
                            : Package
                      : null;
                  const ActionIcon =
                    entry.id === "action-sign-in"
                      ? LogIn
                      : entry.id === "action-cart"
                        ? ShoppingBag
                        : entry.id === "action-orders" || entry.id === "action-track"
                          ? Receipt
                          : null;
                  const Icon = KindIcon ?? PageIcon ?? ActionIcon;

                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${
                        selected ? "bg-brand-pink/15 text-white" : "text-white/80 hover:bg-white/5"
                      }`}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => selectEntry(entry)}
                    >
                      {Icon && <Icon size={15} className="shrink-0 text-white/45" />}
                      <span className="min-w-0 flex-1 truncate">
                        <span className="block truncate">{entry.label}</span>
                        {entry.detail && (
                          <span className="block truncate text-[11px] text-white/40">{entry.detail}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-white/10 px-3 py-2 text-[10px] text-white/35">
          <span>
            <kbd className="rounded border border-white/10 px-1">↑↓</kbd> navigate
            <span className="mx-1.5">·</span>
            <kbd className="rounded border border-white/10 px-1">↵</kbd> open
          </span>
          <span className="hidden sm:inline">
            <kbd className="rounded border border-white/10 px-1">Ctrl</kbd>
            {" + "}
            <kbd className="rounded border border-white/10 px-1">K</kbd>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function useCommandPaletteShortcut(onOpen: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;
      if (!isMod || event.key.toLowerCase() !== "k") return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;

      event.preventDefault();
      onOpen();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, onOpen]);
}

interface SearchTriggerProps {
  onClick: () => void;
  className?: string;
  compact?: boolean;
}

/** Header search chip — opens the command palette. */
export function SearchTrigger({ onClick, className = "", compact = false }: SearchTriggerProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] text-xs text-white/40 transition-colors hover:border-brand-pink/40 hover:text-white/70 ${className}`}
      aria-label="Search (Ctrl+K)"
    >
      <Search size={14} className="shrink-0" />
      {!compact && (
        <>
          <span className="min-w-0 flex-1 truncate text-left">Search shop, marketplace, stores…</span>
          <kbd className="hidden shrink-0 rounded border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[10px] text-white/35 lg:inline">
            Ctrl K
          </kbd>
        </>
      )}
    </button>
  );
}
